import { GeographyHierarchy } from 'hooks/data/search/types';

export interface FlatGeography {
  name: string;
  coordinates: string;
  count: number;
  hasChildren: boolean;
  ancestorNames: string[];
  isSpecial: boolean;
}

export const flattenHierarchy = (
  data: GeographyHierarchy,
  prefix: string | undefined,
  ancestors: string[],
  isSpecial = false
): FlatGeography[] =>
  Object.entries(data)
    .sort(([a], [b]) => a.localeCompare(b))
    .flatMap(([name, item], index) => {
      const coordinates = prefix === undefined ? `${index}` : `${prefix}.${index}`;
      const hasChildren = Object.keys(item.children || {}).length > 0;
      return [
        { name, coordinates, count: item.count, hasChildren, ancestorNames: ancestors, isSpecial },
        ...(hasChildren ? flattenHierarchy(item.children, coordinates, [...ancestors, name], isSpecial) : []),
      ];
    });

export const selectAllChildren = (
  data: GeographyHierarchy,
  baseCoordinates: string,
  targetName: string
): Record<string, string> => {
  const result: Record<string, string> = {};

  const findInData = (geo: GeographyHierarchy, name: string): { count: number; children: GeographyHierarchy } | null => {
    if (geo[name]) return geo[name];
    for (const item of Object.values(geo)) {
      if (item.children && Object.keys(item.children).length > 0) {
        const found = findInData(item.children, name);
        if (found) return found;
      }
    }
    return null;
  };

  const found = findInData(data, targetName);
  if (!found?.children) return result;

  const addChildren = (children: GeographyHierarchy, base: string): void => {
    Object.entries(children)
      .sort(([a], [b]) => a.localeCompare(b))
      .forEach(([childName, childData], index) => {
        const coord = `${base}.${index}`;
        result[coord] = childName;
        if (childData.children && Object.keys(childData.children).length > 0) {
          addChildren(childData.children, coord);
        }
      });
  };

  addChildren(found.children, baseCoordinates);
  return result;
};

export const buildEffectiveCoords = (selected: Record<string, string>, flatList: FlatGeography[]): Set<string> => {
  const result = new Set<string>(Object.keys(selected));
  const itemsByDepth = [...flatList].sort((a, b) => b.coordinates.split('.').length - a.coordinates.split('.').length);
  for (const item of itemsByDepth) {
    if (result.has(item.coordinates) || !item.hasChildren) continue;
    const prefix = item.coordinates + '.';
    const directChildren = flatList.filter((f) => {
      const remainder = f.coordinates.slice(prefix.length);
      return f.coordinates.startsWith(prefix) && !remainder.includes('.');
    });
    if (directChildren.length > 0 && directChildren.every((c) => result.has(c.coordinates))) {
      result.add(item.coordinates);
    }
  }
  return result;
};

export function injectSelfChildren(hierarchy: GeographyHierarchy): GeographyHierarchy {
  const result: GeographyHierarchy = {};

  for (const [name, node] of Object.entries(hierarchy)) {
    const transformedChildren = injectSelfChildren(node.children);
    const childrenSum = Object.values(node.children).reduce((sum, c) => sum + c.count, 0);
    const remainder = node.count - childrenSum;
    const hasChildren = Object.keys(node.children).length > 0;

    const finalChildren =
      hasChildren && remainder > 0
        ? { ...transformedChildren, [name]: { count: remainder, children: {} } }
        : transformedChildren;

    result[name] = { count: node.count, children: finalChildren };
  }

  return result;
}
